// Global JavaScript for Organisca Vibrant Theme - v1.1.2 - Improved cart error handling

// Cart functionality
class CartManager {
  constructor() {
    this.cart = null;
    this.init();
  }

  async init() {
    await this.loadCart();
    this.bindEvents();
  }

  async loadCart() {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
      this.updateCartUI();
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();

      if (data && data.id) {
        await this.loadCart();
        this.showCartNotification('Product added to cart!');
        this.dispatchCartEvent('cart:updated');
      } else {
        throw new Error('Invalid response from cart API: ' + JSON.stringify(data));
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showCartNotification('Error adding product to cart', 'error');
    }
  }

  async updateCart(lineItemId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemId,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.loadCart();
        this.dispatchCartEvent('cart:updated');
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  updateCartUI() {
    const cartCount = document.getElementById('cart-count');
    const mobileCartCount = document.getElementById('mobile-cart-count');
    if (cartCount) {
      cartCount.textContent = this.cart.item_count;
    }
    if (mobileCartCount) {
      mobileCartCount.textContent = this.cart.item_count;
    }
  }

  showCartNotification(message, type = 'success') {
    // Remove any existing notification
    const existingNotification = document.querySelector('.cart-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement('div');
    notification.className = `cart-notification ${type}`;

    // Create notification content with View Cart button
    notification.innerHTML = `
      <div class="cart-notification-content">
        <span class="cart-notification-icon">${type === 'success' ? '✓' : '✗'}</span>
        <span class="cart-notification-message">${message}</span>
      </div>
      ${type === 'success' ? '<a href="/cart" class="cart-notification-btn">View Cart →</a>' : ''}
      <button class="cart-notification-close" onclick="this.parentElement.remove()">×</button>
    `;

    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'success' ? 'linear-gradient(135deg, #2D5016 0%, #4CAF50 100%)' : 'linear-gradient(135deg, #dc3545 0%, #ff6b6b 100%)'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 1rem;
      z-index: 999999;
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      gap: 1rem;
      max-width: 90vw;
      animation: slideUp 0.4s ease;
      font-family: 'Inter', sans-serif;
    `;

    document.body.appendChild(notification);

    // Add styles for inner elements
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideUp {
        from { transform: translateX(-50%) translateY(100px); opacity: 0; }
        to { transform: translateX(-50%) translateY(0); opacity: 1; }
      }
      @keyframes slideDown {
        from { transform: translateX(-50%) translateY(0); opacity: 1; }
        to { transform: translateX(-50%) translateY(100px); opacity: 0; }
      }
      .cart-notification-content {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .cart-notification-icon {
        font-size: 1.2rem;
        font-weight: bold;
      }
      .cart-notification-message {
        font-size: 0.95rem;
        font-weight: 500;
      }
      .cart-notification-btn {
        background: rgba(255,255,255,0.2);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 2rem;
        text-decoration: none;
        font-size: 0.85rem;
        font-weight: 600;
        white-space: nowrap;
        transition: background 0.2s;
      }
      .cart-notification-btn:hover {
        background: rgba(255,255,255,0.3);
      }
      .cart-notification-close {
        background: none;
        border: none;
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0 0.25rem;
        opacity: 0.7;
        transition: opacity 0.2s;
      }
      .cart-notification-close:hover {
        opacity: 1;
      }
      @media (max-width: 480px) {
        .cart-notification {
          bottom: 10px !important;
          left: 10px !important;
          right: 10px !important;
          transform: none !important;
          max-width: none !important;
          flex-wrap: wrap;
          justify-content: center;
        }
        .cart-notification-message {
          font-size: 0.9rem;
        }
      }
    `;
    document.head.appendChild(style);

    setTimeout(() => {
      notification.style.animation = 'slideDown 0.4s ease forwards';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
        style.remove();
      }, 400);
    }, 3000);
  }

  dispatchCartEvent(eventName) {
    const event = new CustomEvent(eventName, {
      detail: { cart: this.cart }
    });
    document.dispatchEvent(event);
  }

  bindEvents() {
    // Add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.add-to-cart-btn') || e.target.closest('.btn-quick-add')) {
        e.preventDefault();
        const form = e.target.closest('form');
        let variantId;

        // Try to find a hidden input named 'id' (common for single variant or JS-updated forms)
        const hiddenInput = form.querySelector('input[type="hidden"][name="id"]');
        if (hiddenInput) {
          variantId = hiddenInput.value;
        } else {
          // Try to find a checked radio button named 'id' (common for multi-variant forms)
          const checkedRadio = form.querySelector('input[type="radio"][name="id"]:checked');
          if (checkedRadio) {
            variantId = checkedRadio.value;
          }
        }

        if (variantId) {
          this.addToCart(variantId);
        } else {
          console.error('No variant ID found in form');
          this.showCartNotification('Please select a variant', 'error');
        }
      }
    });

    // Cart count updates
    document.addEventListener('cart:updated', () => {
      this.updateCartUI();
    });
  }
}

// Search functionality
class SearchManager {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    const searchBtn = document.querySelector('.search-btn');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        this.openSearch();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === '/' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        this.openSearch();
      }
    });
  }

  openSearch() {
    // Create search modal
    const modal = document.createElement('div');
    modal.className = 'search-modal';
    modal.innerHTML = `
      <div class="search-modal-content">
        <div class="search-input-container">
          <input type="text" placeholder="Search products..." class="search-input" autofocus>
          <button class="search-close">×</button>
        </div>
        <div class="search-results"></div>
      </div>
    `;

    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 10vh;
    `;

    document.body.appendChild(modal);

    const searchInput = modal.querySelector('.search-input');
    const searchResults = modal.querySelector('.search-results');
    const closeBtn = modal.querySelector('.search-close');

    // Search functionality
    let searchTimeout;
    searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.performSearch(e.target.value, searchResults);
      }, 300);
    });

    // Close modal
    closeBtn.addEventListener('click', () => {
      document.body.removeChild(modal);
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  }

  async performSearch(query, resultsContainer) {
    if (query.length < 2) {
      resultsContainer.innerHTML = '';
      return;
    }

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await response.json();

      this.displaySearchResults(data.resources.results.products, resultsContainer);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  displaySearchResults(products, container) {
    if (products.length === 0) {
      container.innerHTML = '<p class="no-results">No products found</p>';
      return;
    }

    container.innerHTML = products.map(product => `
      <div class="search-result-item">
        <img src="${product.image}" alt="${product.title}" class="search-result-image">
        <div class="search-result-info">
          <h3 class="search-result-title">${product.title}</h3>
          <p class="search-result-price">₹${product.price}</p>
        </div>
      </div>
    `).join('');

    // Add click handlers
    container.querySelectorAll('.search-result-item').forEach(item => {
      item.addEventListener('click', () => {
        window.location.href = `/products/${item.querySelector('.search-result-title').textContent.toLowerCase().replace(/\s+/g, '-')}`;
      });
    });
  }
}

// Wishlist functionality
class WishlistManager {
  constructor() {
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateWishlistUI();
    this.updateHeaderCount();
  }

  loadWishlist() {
    const saved = localStorage.getItem('organisca-wishlist');
    return saved ? JSON.parse(saved) : [];
  }

  saveWishlist() {
    localStorage.setItem('organisca-wishlist', JSON.stringify(this.wishlist));
  }

  toggleWishlist(productId) {
    const index = this.wishlist.indexOf(productId);
    if (index > -1) {
      this.wishlist.splice(index, 1);
    } else {
      this.wishlist.push(productId);
    }
    this.saveWishlist();
    this.updateWishlistUI();
    this.updateHeaderCount();
  }

  updateWishlistUI() {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const productId = btn.dataset.productId;
      const heartIcon = btn.querySelector('.heart-icon');

      if (this.wishlist.includes(productId)) {
        if (heartIcon) heartIcon.textContent = '❤️';
        btn.classList.add('active');
      } else {
        if (heartIcon) heartIcon.textContent = '🤍';
        btn.classList.remove('active');
      }
    });
  }

  updateHeaderCount() {
    const count = this.wishlist.length;
    document.querySelectorAll('#wishlist-count, #mobile-wishlist-count').forEach(el => {
      el.textContent = count;
    });
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-btn')) {
        e.preventDefault();
        const btn = e.target.closest('.wishlist-btn');
        const productId = btn.dataset.productId;
        this.toggleWishlist(productId);
      }
    });
  }
}

// Smooth scrolling
function smoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
}

// Lazy loading images
function lazyLoadImages() {
  const images = document.querySelectorAll('img[data-src]');
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.classList.remove('lazy');
        imageObserver.unobserve(img);
      }
    });
  });

  images.forEach(img => imageObserver.observe(img));
}

// Form validation
function validateForms() {
  const forms = document.querySelectorAll('form[data-validate]');
  forms.forEach(form => {
    form.addEventListener('submit', (e) => {
      if (!form.checkValidity()) {
        e.preventDefault();
        e.stopPropagation();
      }
      form.classList.add('was-validated');
    });
  });
}

// Initialize everything when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
  // Initialize managers
  window.cartManager = new CartManager();
  window.searchManager = new SearchManager();
  window.wishlistManager = new WishlistManager();

  // Initialize other features
  smoothScroll();
  lazyLoadImages();
  validateForms();

  // Add CSS animations
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    
    @keyframes slideOut {
      from { transform: translateX(0); opacity: 1; }
      to { transform: translateX(100%); opacity: 0; }
    }
    
    .search-modal-content {
      background: white;
      border-radius: 1rem;
      padding: 2rem;
      width: 90%;
      max-width: 600px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
    }
    
    .search-input-container {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    
    .search-input {
      flex: 1;
      padding: 1rem;
      border: 2px solid #e5e7eb;
      border-radius: 0.5rem;
      font-size: 1rem;
    }
    
    .search-close {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: #6b7280;
    }
    
    .search-result-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }
    
    .search-result-item:hover {
      background-color: #f9fafb;
    }
    
    .search-result-image {
      width: 50px;
      height: 50px;
      object-fit: cover;
      border-radius: 0.5rem;
    }
    
    .search-result-title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
    }
    
    .search-result-price {
      font-size: 0.875rem;
      color: #22c55e;
      font-weight: 600;
      margin: 0;
    }
    
    .no-results {
      text-align: center;
      color: #6b7280;
      padding: 2rem;
    }
  `;
  document.head.appendChild(style);
});

// Export for use in other scripts
window.OrganiscaTheme = {
  CartManager,
  SearchManager,
  WishlistManager
};
